import type { UseFormReturn } from 'react-hook-form';
import { Badge } from '../../../components/ui/Badge';
import { Section } from '../../../components/ui/Section';
import { str } from '../../../platform/api/utils';
import {
  type EditForm,
  TYPE_LABELS,
  STATUS_LABELS,
  STATUS_VARIANTS,
  VISIBILITY_LABELS,
  VISIBILITY_VARIANTS,
  Field,
  fmtDate,
  dlGrid,
  Dt,
  Dd,
  inputCss,
  primaryBtn,
  secondaryBtn,
} from './obligation-detail.support';
import { usePermissions } from '../../../platform/auth/permissions';

export function ObligationDadosTab(props: {
  editing: boolean;
  form: UseFormReturn<EditForm>;
  o: Record<string, unknown>;
  tp: string;
  st: string;
  vis: string;
  updatePending: boolean;
  onCancelEdit: () => void;
  onSave: (data: EditForm) => void;
}) {
  const { editing, form, o, tp, st, vis, updatePending, onCancelEdit, onSave } = props;
  const canRestricted = usePermissions().can('obligations.restricted.read');
  return (
    <>
      {editing ? (
        <Section title="Editar obrigação">
          <p
            style={{
              fontSize: '0.7rem',
              color: 'var(--warning)',
              margin: '0 0 1rem',
              padding: '0.4rem 0.6rem',
              background: 'var(--warning-soft)',
              borderRadius: 8,
              border: '1px solid var(--warning-soft)',
            }}
          >
            Não registre fundamento, passo ritualístico ou segredo litúrgico.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem 1.5rem' }}>
            <Field label="Título" error={form.formState.errors.title?.message}>
              <input {...form.register('title')} style={inputCss} />
            </Field>
            <Field label="Tipo">
              <select {...form.register('type')} style={inputCss}>
                {Object.entries(TYPE_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Visibilidade">
              <select {...form.register('visibility')} style={inputCss}>
                {Object.entries(VISIBILITY_LABELS)
                  .filter(([v]) => canRestricted || v !== 'RESTRICTED')
                  .map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
              </select>
            </Field>
            <div />
            <Field label="Data prevista">
              <input {...form.register('expectedDate')} type="date" style={inputCss} />
            </Field>
            <Field label="Data concluída">
              <input {...form.register('completedDate')} type="date" style={inputCss} />
            </Field>
            <Field label="Descrição" error={undefined}>
              <textarea
                {...form.register('description')}
                style={{ ...inputCss, minHeight: 60, resize: 'vertical' }}
              />
            </Field>
            {canRestricted && (
              <Field label="Notas restritas" error={undefined}>
                <textarea
                  {...form.register('restrictedNotes')}
                  style={{ ...inputCss, minHeight: 60, resize: 'vertical' }}
                />
              </Field>
            )}
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: '0.85rem',
                gridColumn: '1 / -1',
              }}
            >
              <input type="checkbox" {...form.register('visibleInMemberApp')} />
              Visível no app do membro (somente versão segura)
            </label>
            <Field label="Título seguro (app)">
              <input
                {...form.register('safeDisplayTitle')}
                style={inputCss}
                placeholder="Texto não sensível para o app"
              />
            </Field>
            <Field label="Resumo seguro (app)">
              <textarea
                {...form.register('safeDisplaySummary')}
                style={{ ...inputCss, minHeight: 60, resize: 'vertical' }}
                placeholder="Resumo genérico para o membro"
              />
            </Field>
          </div>
          <div
            style={{
              display: 'flex',
              gap: '0.5rem',
              marginTop: '1rem',
              justifyContent: 'flex-end',
            }}
          >
            <button type="button" onClick={onCancelEdit} style={secondaryBtn}>
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => {
                void form.handleSubmit(onSave)();
              }}
              disabled={updatePending}
              style={primaryBtn}
            >
              {updatePending ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </Section>
      ) : (
        <>
          <Section title="Dados administrativos">
            <dl style={dlGrid}>
              <Dt>Título</Dt>
              <Dd>{str(o.title)}</Dd>
              <Dt>Tipo</Dt>
              <Dd>{TYPE_LABELS[tp] ?? tp}</Dd>
              <Dt>Status</Dt>
              <Dd>
                <Badge label={STATUS_LABELS[st] ?? st} variant={STATUS_VARIANTS[st] ?? 'default'} />
              </Dd>
              <Dt>Visibilidade</Dt>
              <Dd>
                <Badge
                  label={VISIBILITY_LABELS[vis] ?? vis}
                  variant={VISIBILITY_VARIANTS[vis] ?? 'default'}
                />
              </Dd>
              <Dt>Data prevista</Dt>
              <Dd>{fmtDate(o.expectedDate)}</Dd>
              <Dt>Data concluída</Dt>
              <Dd>{fmtDate(o.completedDate)}</Dd>
              <Dt>App do membro</Dt>
              <Dd>{o.visibleInMemberApp ? 'Visível (versão segura)' : 'Oculto'}</Dd>
              {str(o.safeDisplayTitle) && (
                <>
                  <Dt>Título seguro</Dt>
                  <Dd>{str(o.safeDisplayTitle)}</Dd>
                </>
              )}
              {str(o.safeDisplaySummary) && (
                <>
                  <Dt>Resumo seguro</Dt>
                  <Dd>{str(o.safeDisplaySummary)}</Dd>
                </>
              )}
              {str(o.description) && (
                <>
                  <Dt>Descrição</Dt>
                  <Dd>{str(o.description)}</Dd>
                </>
              )}
            </dl>
          </Section>
          {str(o.restrictedNotes) && (
            <Section title="Notas restritas">
              <p
                style={{
                  fontSize: '0.75rem',
                  color: 'var(--warning)',
                  marginBottom: '0.5rem',
                  padding: '0.3rem 0.5rem',
                  background: 'var(--warning-soft)',
                  borderRadius: 8,
                  border: '1px solid var(--warning-soft)',
                }}
              >
                Estas anotações são confidenciais. Este campo está visível apenas para usuários
                autorizados.
              </p>
              <p
                style={{
                  fontSize: '0.85rem',
                  color: 'var(--muted)',
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {str(o.restrictedNotes)}
              </p>
            </Section>
          )}
          <Section title="Metadados">
            <dl style={dlGrid}>
              <Dt>Criada em</Dt>
              <Dd>{fmtDate(o.createdAt)}</Dd>
              <Dt>Atualizada em</Dt>
              <Dd>{fmtDate(o.updatedAt)}</Dd>
              <Dt>Arquivada em</Dt>
              <Dd>{fmtDate(o.archivedAt)}</Dd>
            </dl>
          </Section>
        </>
      )}
    </>
  );
}
